import { useState } from "react";
import { Globe, ChevronDown, Copy, Trash2, FileUp, FileText, KeyRound, Users, UserPlus, MailPlus, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls = "w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";

export default function ClientPortalPanel({ client }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [staffPicked, setStaffPicked] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [docTitle, setDocTitle] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showActivity, setShowActivity] = useState(false);

  const load = async () => {
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get(`/admin/clients/${client.id}/portal`), api.get("/staff"),
      ]);
      setData(p);
      setStaff((s || []).filter((x) => x.active));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggle = () => {
    setOpen((o) => !o);
    if (!open) load();
  };

  const addLogin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/clients/${client.id}/portal`, form);
      toast.success(data.message);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async () => {
    if (!form.email.trim()) {
      toast.error("Type their email first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/clients/${client.id}/portal-invite`, {
        name: form.name, email: form.email, origin: window.location.origin,
      });
      toast.success(data.message);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeLogin = async (l) => {
    if (!window.confirm(`Remove portal access for ${l.email}?`)) return;
    try {
      await api.delete(`/admin/clients/${client.id}/portal/${l.id}`);
      toast.success("Login removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const disableAll = async () => {
    if (!window.confirm("Disable ALL portal logins for this client?")) return;
    try {
      const { data } = await api.delete(`/admin/clients/${client.id}/portal`);
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const giveStaffAccess = async () => {
    if (!staffPicked.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/clients/${client.id}/portal-staff`, { staff_ids: staffPicked });
      toast.success(data.message);
      setStaffPicked([]);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadDoc = async () => {
    if (!docFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      await api.post(`/admin/clients/${client.id}/portal-docs?title=${encodeURIComponent(docTitle)}`, fd);
      toast.success("Document added to the client portal");
      setDocTitle("");
      setDocFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteDoc = async (d) => {
    try {
      await api.delete(`/admin/clients/${client.id}/portal-docs/${d.id}`);
      toast.success("Document removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleReport = async (ev, visible) => {
    try {
      const { data } = await api.post(`/admin/events/${ev.id}/portal-report`, { published: visible });
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/portal/login`);
    toast.success("Portal link copied — send it with the person's login email + password");
  };

  const staffWithAccess = new Set((data?.logins || []).map((l) => l.staff_user_id).filter(Boolean));

  return (
    <div className="w-full">
      <button onClick={toggle} data-testid={`portal-toggle-${client.id}`}
        className="h-9 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-xs font-bold inline-flex items-center gap-1.5">
        <Globe className="h-3.5 w-3.5" /> Client portal
        {(data?.logins?.length > 0) && <span className="text-emerald-700">· {data.logins.length} login{data.logins.length === 1 ? "" : "s"}</span>}
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-4" data-testid={`portal-panel-${client.id}`}>
          {!data ? (
            <p className="text-xs text-slate-400">Loading…</p>
          ) : (
            <>
              <div>
                <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5 mb-2">
                  <KeyRound className="h-3.5 w-3.5 text-[#005EB8]" /> Who can log in ({data.logins.length})
                </p>
                {(data.logins || []).map((l) => (
                  <div key={l.id} className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0" data-testid={`portal-login-row-${l.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700 truncate">
                        {l.name || l.email}
                        {l.staff_user_id && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-sky-50 text-[#005EB8] text-[10px] font-bold">WEMS staff</span>}
                        {l.invite_pending && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold">invite sent</span>}
                        {!l.invite_pending && !l.password_set && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-red-50 text-[#D5281B] text-[10px] font-bold">no password</span>}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {l.email}{l.last_login_at && ` · last in ${new Date(l.last_login_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`}
                      </p>
                    </div>
                    <button onClick={() => removeLogin(l)} data-testid={`portal-login-del-${l.id}`} title="Remove this login"
                      className="h-8 w-8 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 transition-colors shrink-0">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {(client.email || (client.contacts || []).some((ct) => ct.email)) && (
                  <select value="" onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__company") {
                        setForm((f) => ({ ...f, name: client.name, email: client.email }));
                      } else {
                        const ct = (client.contacts || []).find((x) => x.contact_id === v);
                        if (ct) setForm((f) => ({ ...f, name: ct.name || "", email: ct.email || "" }));
                      }
                    }}
                    data-testid="portal-contact-pick"
                    className="h-10 w-full rounded-lg border border-slate-300 px-3 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8] mb-2">
                    <option value="">Pick someone from {client.name}'s people — fills their details in…</option>
                    {client.email && <option value="__company">{client.name} — main company email ({client.email})</option>}
                    {(client.contacts || []).filter((ct) => ct.email).map((ct) => (
                      <option key={ct.contact_id} value={ct.contact_id}>{ct.name}{ct.role ? ` — ${ct.role}` : ""} ({ct.email})</option>
                    ))}
                  </select>
                )}
                <div className="grid sm:grid-cols-3 gap-2 mt-2">
                  <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Contact name" data-testid="portal-form-name" className={inputCls} />
                  <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="Login email" data-testid="portal-form-email" className={inputCls} />
                  <input value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    type="text" placeholder="Password (only if adding manually)" data-testid="portal-form-password" className={inputCls} />
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <button onClick={sendInvite} disabled={busy} data-testid="portal-invite"
                    className="h-9 px-4 rounded-lg bg-[#007F3B] hover:bg-[#006631] disabled:opacity-50 text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                    <MailPlus className="h-3.5 w-3.5" /> Send invite email
                  </button>
                  <button onClick={addLogin} disabled={busy} data-testid="portal-save"
                    className="h-9 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                    <UserPlus className="h-3.5 w-3.5" /> Add manually
                  </button>
                  <button onClick={copyLink} data-testid="portal-copy-link"
                    className="h-9 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                    <Copy className="h-3.5 w-3.5" /> Copy portal link
                  </button>
                  {data.logins.length > 0 && (
                    <button onClick={disableAll} data-testid="portal-disable"
                      className="h-9 px-3 rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 text-xs font-bold transition-colors">
                      Disable all access
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">Invite emails them a link to set their own password. Manual = you choose the password and share it yourself.</p>
              </div>

              <div>
                <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5 mb-1">
                  <Users className="h-3.5 w-3.5 text-[#005EB8]" /> Give WEMS staff access
                </p>
                <p className="text-[11px] text-slate-400 mb-2">Ticked staff can log into this client's portal with their normal staff email &amp; password.</p>
                <div className="flex gap-2 mb-2">
                  <button onClick={() => setStaffPicked(staff.map((s) => s.id))} data-testid="portal-staff-all"
                    className="h-8 px-3 rounded-lg border border-slate-300 text-[11px] font-bold text-slate-600 hover:border-[#005EB8] transition-colors">Select all</button>
                  <button onClick={() => setStaffPicked([])} data-testid="portal-staff-none"
                    className="h-8 px-3 rounded-lg border border-slate-300 text-[11px] font-bold text-slate-600 hover:border-[#005EB8] transition-colors">Clear</button>
                </div>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-50" data-testid="portal-staff-list">
                  {staff.map((s) => (
                    <label key={s.id} className="flex items-center gap-2.5 px-3 py-2 text-xs cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={staffPicked.includes(s.id)} disabled={staffWithAccess.has(s.id)}
                        onChange={(e) => setStaffPicked((p) => e.target.checked ? [...p, s.id] : p.filter((x) => x !== s.id))}
                        data-testid={`portal-staff-${s.id}`} className="h-4 w-4 accent-[#005EB8]" />
                      <span className="font-semibold text-slate-700">{s.name}</span>
                      <span className="text-slate-400 truncate">{s.email}</span>
                      {staffWithAccess.has(s.id) && <span className="ml-auto text-[10px] font-bold text-emerald-700 shrink-0">has access</span>}
                    </label>
                  ))}
                </div>
                <button onClick={giveStaffAccess} disabled={busy || !staffPicked.length} data-testid="portal-staff-apply"
                  className="mt-2 h-9 px-4 rounded-lg bg-[#007F3B] hover:bg-[#006631] disabled:opacity-50 text-white text-xs font-bold transition-colors">
                  Give access to {staffPicked.length || "selected"} staff
                </button>
              </div>

              <div>
                <p className="text-xs font-bold text-slate-600 mb-1">Event reports &amp; live view</p>
                <p className="text-[11px] text-slate-400 mb-2">Shared automatically — hide any you don't want the client to see. Hiding also removes their live view link.</p>
                {data.events.length === 0 ? (
                  <p className="text-[11px] text-slate-400">No invoiced events yet — events appear here once an invoice or quote is raised for this client.</p>
                ) : data.events.map((ev) => (
                  <div key={ev.id} className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0" data-testid={`portal-event-${ev.id}`}>
                    <span className="text-xs text-slate-700 flex-1">{ev.name}{ev.event_date ? ` · ${ev.event_date}` : ""}</span>
                    <button onClick={() => toggleReport(ev, !ev.portal_report_published)} data-testid={`portal-report-toggle-${ev.id}`}
                      className={`h-8 px-3 rounded-lg text-[11px] font-bold transition-colors ${ev.portal_report_published ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-white border border-slate-300 text-slate-500 hover:border-[#005EB8]"}`}>
                      {ev.portal_report_published ? "Visible — hide" : "Hidden — show"}
                    </button>
                  </div>
                ))}
              </div>

              <div>
                <p className="text-xs font-bold text-slate-600 mb-1">Documents</p>
                <p className="text-[11px] text-slate-400 mb-2">Medical plans link in automatically. Upload anything else — the client can send files back to you too.</p>
                <div className="flex gap-2 flex-wrap items-center">
                  <input value={docTitle} onChange={(e) => setDocTitle(e.target.value)}
                    placeholder="Title (optional)" data-testid="portal-doc-title" className={`${inputCls} sm:max-w-44`} />
                  <label className="h-11 px-3 rounded-lg border border-dashed border-slate-300 text-xs font-bold text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] inline-flex items-center gap-1.5 cursor-pointer transition-colors">
                    <FileUp className="h-3.5 w-3.5" /> {docFile ? docFile.name : "Choose file"}
                    <input type="file" className="hidden" data-testid="portal-doc-file"
                      onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
                  </label>
                  <button onClick={uploadDoc} disabled={busy || !docFile} data-testid="portal-doc-upload"
                    className="h-9 px-4 rounded-lg bg-[#007F3B] hover:bg-[#006631] disabled:opacity-50 text-white text-xs font-bold transition-colors">
                    Add to portal
                  </button>
                </div>
                {(data.documents || []).map((d) => (
                  <div key={d.id} className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0" data-testid={`portal-admin-doc-${d.id}`}>
                    <FileText className="h-3.5 w-3.5 text-[#005EB8] shrink-0" />
                    <span className="text-xs text-slate-700 flex-1 truncate">
                      {d.title} <span className="text-slate-400">({d.filename})</span>
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${d.direction === "from_client" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                        {d.direction === "from_client" ? `From client${d.uploaded_by ? ` · ${d.uploaded_by}` : ""}` : "From us"}
                      </span>
                    </span>
                    <button onClick={() => deleteDoc(d)} data-testid={`portal-doc-delete-${d.id}`}
                      className="h-8 w-8 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 transition-colors shrink-0">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>

              <div>
                <button onClick={() => setShowActivity((s) => !s)} data-testid="portal-activity-toggle"
                  className="text-xs font-bold text-slate-600 inline-flex items-center gap-1.5 hover:text-[#005EB8] transition-colors">
                  <Eye className="h-3.5 w-3.5" /> Portal activity ({(data.activity || []).length})
                  <ChevronDown className={`h-3 w-3 transition-transform ${showActivity ? "rotate-180" : ""}`} />
                </button>
                {showActivity && (
                  <div className="mt-2 rounded-lg border border-slate-200 bg-white divide-y divide-slate-50 max-h-56 overflow-y-auto" data-testid="portal-activity-list">
                    {(data.activity || []).length === 0 ? (
                      <p className="text-[11px] text-slate-400 px-3 py-3">No activity yet — logins, page views, downloads, uploads and quote requests are recorded here.</p>
                    ) : data.activity.map((a) => (
                      <div key={a.id} className="px-3 py-2" data-testid={`portal-activity-${a.id}`}>
                        <p className="text-xs font-semibold text-slate-700">{a.name || a.email} <span className="font-normal text-slate-500">— {a.action}{a.detail ? `: ${a.detail}` : ""}</span></p>
                        <p className="text-[10px] text-slate-400">{a.email} · {a.at ? new Date(a.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
